// Booklet imposition preview: draws the real 12x18 press sheet with `nUp` open
// spreads laid out on it (each spread folds in half), plus the binding graphic.
export default function BookletCanvas({ openW = 17, openH = 11, foldedW = 8.5, foldedH = 11, nUp = 1, binding = "saddle" }) {
  const c = "#2495D3";
  const SW = 12, SH = 18;                 // press sheet inches (portrait)

  // Pick the orientation that fits the most spreads on the sheet.
  const o1 = Math.floor(SW / openW) * Math.floor(SH / openH);
  const o2 = Math.floor(SW / openH) * Math.floor(SH / openW);
  let cols, rows, pieceWin, pieceHin, foldVertical;
  if (o1 >= o2) {
    cols = Math.max(1, Math.floor(SW / openW)); rows = Math.max(1, Math.floor(SH / openH));
    pieceWin = openW; pieceHin = openH; foldVertical = true;
  } else {
    cols = Math.max(1, Math.floor(SW / openH)); rows = Math.max(1, Math.floor(SH / openW));
    pieceWin = openH; pieceHin = openW; foldVertical = false;
  }

  const W = 360;
  const scale = 250 / SH;                  // px per inch
  const sheetWpx = SW * scale, sheetHpx = SH * scale;
  const sheetX = (W - sheetWpx) / 2, sheetY = 30;
  const H = sheetHpx + 60;
  const pieceWpx = pieceWin * scale, pieceHpx = pieceHin * scale;

  const bindingOnFold = (fx1, fy1, fx2, fy2, kp) => {
    const els = [];
    const len = Math.hypot(fx2 - fx1, fy2 - fy1) || 1;
    const ux = (fx2 - fx1) / len, uy = (fy2 - fy1) / len;   // along fold
    if (binding === "perfect") {
      els.push(<line key={kp + "spine"} x1={fx1} y1={fy1} x2={fx2} y2={fy2} stroke="#0F172A" strokeWidth={6} opacity="0.85" strokeLinecap="round" />);
    } else if (binding === "saddle") {
      for (let i = 1; i <= 3; i++) {
        const t = i / 4, mx = fx1 + ux * len * t, my = fy1 + uy * len * t;
        els.push(<line key={kp + "st" + i} x1={mx - uy * 5} y1={my + ux * 5} x2={mx + uy * 5} y2={my - ux * 5} stroke="#0F172A" strokeWidth={3} strokeLinecap="round" />);
      }
    } else if (binding === "spiral") {
      const n = Math.max(6, Math.round(len / 12));
      for (let i = 0; i < n; i++) {
        const t = (i + 0.5) / n, mx = fx1 + ux * len * t, my = fy1 + uy * len * t;
        els.push(<circle key={kp + "sp" + i} cx={mx} cy={my} r={3.5} fill="none" stroke={c} strokeWidth="1.4" />);
      }
    } else if (binding === "wireo") {
      const n = Math.max(5, Math.round(len / 16));
      for (let i = 0; i < n; i++) {
        const t = (i + 0.5) / n, mx = fx1 + ux * len * t, my = fy1 + uy * len * t;
        els.push(<rect key={kp + "w" + i} x={mx - 4} y={my - 3} width={8} height={6} rx="3" fill="none" stroke={c} strokeWidth="1.4" />);
      }
    }
    return els;
  };

  const pieces = [];
  let count = 0;
  for (let r = 0; r < rows && count < nUp; r++) {
    for (let cc = 0; cc < cols && count < nUp; cc++) {
      const px = sheetX + cc * pieceWpx, py = sheetY + r * pieceHpx;
      pieces.push(<rect key={`p${count}`} x={px} y={py} width={pieceWpx} height={pieceHpx} fill="#fff" stroke="#CBD5E1" strokeWidth="1.1" />);
      if (foldVertical) {
        const foldX = px + pieceWpx / 2;
        pieces.push(<rect key={`l${count}`} x={px} y={py} width={pieceWpx / 2} height={pieceHpx} fill={c} fillOpacity="0.05" />);
        pieces.push(<rect key={`r${count}`} x={foldX} y={py} width={pieceWpx / 2} height={pieceHpx} fill={c} fillOpacity="0.10" />);
        pieces.push(<line key={`f${count}`} x1={foldX} y1={py} x2={foldX} y2={py + pieceHpx} stroke="#94A3B8" strokeWidth="1" strokeDasharray="4 3" />);
        pieces.push(<text key={`a${count}`} x={px + pieceWpx / 4} y={py + pieceHpx / 2} textAnchor="middle" fontSize="8" fill="#64748B" className="font-mono">{foldedW}×{foldedH}"</text>);
        pieces.push(<text key={`b${count}`} x={foldX + pieceWpx / 4} y={py + pieceHpx / 2} textAnchor="middle" fontSize="8" fill="#64748B" className="font-mono">{foldedW}×{foldedH}"</text>);
        pieces.push(...bindingOnFold(foldX, py, foldX, py + pieceHpx, `bd${count}`));
      } else {
        const foldY = py + pieceHpx / 2;
        pieces.push(<rect key={`t${count}`} x={px} y={py} width={pieceWpx} height={pieceHpx / 2} fill={c} fillOpacity="0.05" />);
        pieces.push(<rect key={`u${count}`} x={px} y={foldY} width={pieceWpx} height={pieceHpx / 2} fill={c} fillOpacity="0.10" />);
        pieces.push(<line key={`f${count}`} x1={px} y1={foldY} x2={px + pieceWpx} y2={foldY} stroke="#94A3B8" strokeWidth="1" strokeDasharray="4 3" />);
        pieces.push(<text key={`a${count}`} x={px + pieceWpx / 2} y={py + pieceHpx / 4} textAnchor="middle" fontSize="8" fill="#64748B" className="font-mono">{foldedW}×{foldedH}"</text>);
        pieces.push(<text key={`b${count}`} x={px + pieceWpx / 2} y={foldY + pieceHpx / 4} textAnchor="middle" fontSize="8" fill="#64748B" className="font-mono">{foldedW}×{foldedH}"</text>);
        pieces.push(...bindingOnFold(px, foldY, px + pieceWpx, foldY, `bd${count}`));
      }
      count++;
    }
  }

  return (
    <div className="mt-2" data-testid="booklet-canvas">
      <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1">
        <span>Open spread · {openW}×{openH}"</span>
        <span data-testid="booklet-nup">{nUp}-up / 12×18" sheet</span>
      </div>
      <svg width={W} height={H} className="border border-slate-200 rounded-sm bg-slate-50" viewBox={`0 0 ${W} ${H}`}>
        {/* 12x18 press sheet (waste shown as the gray area) */}
        <rect x={sheetX} y={sheetY} width={sheetWpx} height={sheetHpx} fill="#F1F5F9" stroke="#94A3B8" strokeWidth="1.3" rx="2" />
        {pieces}
      </svg>
      <div className="text-[10px] text-slate-400 mt-1 font-mono">Sheet 12×18 · {nUp} spread(s)/sheet · center = fold · {binding}</div>
    </div>
  );
}
